import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Divider, alpha, Button, FormControl, InputLabel, Select,
  MenuItem, TextField, Chip, LinearProgress
} from '@mui/material';
import {
  Assessment as ReportIcon,
  AccessTime as ClockIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const PunchCardReport = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/reports/users');
      const usersList = response.data.data || [];
      setUsers(usersList);
      
      // If only one user (self for non-admin), auto-select
      if (usersList.length === 1) {
        setSelectedUser(usersList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Auto-generate report when user is selected (for non-admin with single user)
  useEffect(() => {
    if (selectedUser && users.length === 1) {
      handleGenerateReport();
    }
  }, [selectedUser]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!selectedUser) return;

    setGenerating(true);
    try {
      const params = new URLSearchParams({ user_id: selectedUser, period });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await api.get(`/reports/punchcard?${params}`);
      setReport(response.data.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setGenerating(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'idle': return 'warning';
      case 'auto_closed': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Working';
      case 'completed': return 'Completed';
      case 'idle': return 'Idle';
      case 'auto_closed': return 'Auto Closed';
      default: return status;
    }
  };

  // Calculate productivity
  const calculateProductivity = (hours, period) => {
    let expectedHours;
    switch (period) {
      case 'daily': expectedHours = 8; break;
      case 'weekly': expectedHours = 40; break;
      case 'monthly': expectedHours = 160; break;
      case 'yearly': expectedHours = 2000; break;
      default: expectedHours = 8;
    }
    return Math.min(100, Math.round((parseFloat(hours) / expectedHours) * 100));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportIcon sx={{ fontSize: 32 }} />
          Punch Card Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View detailed punch card reports for employees
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={selectedUser}
                  label="Select Employee"
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <MenuItem value="daily">Today</MenuItem>
                  <MenuItem value="weekly">This Week</MenuItem>
                  <MenuItem value="monthly">This Month</MenuItem>
                  <MenuItem value="yearly">This Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={period === 'custom' ? 3 : 5}>
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={!selectedUser || generating}
                startIcon={generating ? <CircularProgress size={16} /> : <ReportIcon />}
                fullWidth
                sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
              >
                {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report Content */}
      {report && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{report.user.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{report.user.email}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ClockIcon color="success" />
                    <Typography variant="subtitle2" color="text.secondary">Total Hours</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {report.summary.totalHours}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.summary.daysWorked} days worked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarIcon color="info" />
                    <Typography variant="subtitle2" color="text.secondary">Sessions</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {report.summary.totalSessions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg {report.summary.averageHoursPerDay}h/day
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingIcon color="secondary" />
                    <Typography variant="subtitle2" color="text.secondary">Productivity</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                    {calculateProductivity(report.summary.totalHours, period)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={calculateProductivity(report.summary.totalHours, period)}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Daily Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon />
                Daily Breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>First Punch In</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Punch Out</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sessions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.dailyBreakdown?.map((day) => (
                      <TableRow key={day.date} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(day.date)}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatTime(day.firstPunchIn)}</TableCell>
                        <TableCell>{formatTime(day.lastPunchOut)}</TableCell>
                        <TableCell>
                          <Chip label={day.sessions.length} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {day.totalHours.toFixed(2)}h
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {report.dailyBreakdown?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No punch card data for this period</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Detailed Sessions */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ClockIcon />
                All Sessions
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Punch In</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Punch Out</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.punchCards?.map((pc) => (
                      <TableRow key={pc.id} hover>
                        <TableCell>{formatDate(pc.punch_date)}</TableCell>
                        <TableCell>{formatTime(pc.punch_in)}</TableCell>
                        <TableCell>{formatTime(pc.punch_out)}</TableCell>
                        <TableCell>
                          {pc.total_hours ? `${parseFloat(pc.total_hours).toFixed(2)}h` : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(pc.status)}
                            size="small"
                            color={getStatusColor(pc.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                            {pc.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {report.punchCards?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No sessions found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {!report && !generating && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ReportIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select an employee and click "Generate Report" to view their punch card data
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PunchCardReport;
